import type { IListenable } from "../../interfaces/IListenable";
import type { IMeasurable } from "../../interfaces/IMeasurable";
import { EventEmitter } from "../Core/EventEmitter";
import { throttle } from "@/map/utils/EventUtils";
import type { EventHandler } from "../../interfaces/EventHandler";

const DEFAULT_USE_THROTTLE = true; // 是否默认使用节流函数
const DEFAULT_THROTTLE_WAIT = 16; // 默认节流函数的等待时间，单位为毫秒
const DEFAULT_PREVENT_DEFAULT = true; // 是否默认阻止滚轮事件的默认行为（如页面滚动）
const DEFAULT_PREVENT_CTRL_WHEEL_ZOOM = true; // 是否默认禁用 ctrl + 滚轮的浏览器缩放

/**
 * 鼠标滚轮事件数据格式
 */
export type WheelEventData = {
    originalEvent: WheelEvent; // 原始滚轮事件
    mouseX: number; // 鼠标指针相对于 domWrapper 的 x 坐标
    mouseY: number; // 鼠标指针相对于 domWrapper 的 y 坐标
    deltaX: number; // 水平滚动量
    deltaY: number; // 垂直滚动量
    deltaZ: number; // Z轴滚动量
    deltaMode: number; // 滚动单位 (0:像素, 1:行, 2:页)
    ctrlKey: boolean; // 是否按下 Ctrl 键
};

/**
 * WheelManager 发射的事件类型
 */
export enum WheelEvents {
    /** 鼠标滚轮滚动事件，抛出 {@link WheelEventData} 类型事件对象 */
    SCROLL = 'wheel-scroll',
}

interface WheelEventMap {
    [WheelEvents.SCROLL]: WheelEventData;
}

/**
 * 鼠标滚轮事件管理器
 * 负责监听指定 DOM 元素上的滚轮事件，并提供节流和自定义事件发射功能
 */
export class WheelManager {

    protected domWrapper: IListenable & IMeasurable;
    private wheelEventsEmitter = new EventEmitter<WheelEventMap>(); // 事件发射器

    // 事件处理器引用
    private _wheelHandler: (e: WheelEvent) => void;
    private _throttledWheelHandler: (e: WheelEvent) => void;

    private useThrottle: boolean; // 当前是否使用节流
    private throttleWait: number; // 当前节流等待时间
    private preventDefault: boolean; // 是否阻止滚轮事件的默认行为
    private preventCtrlWheelZoom: boolean; // 是否禁用 ctrl+滚轮浏览器缩放

    /**
     * 构造函数
     * @param target - 监听滚轮事件的 DOM 元素，默认为 document.body
     * @param options - 配置选项
     * @param options.useThrottle - 是否启用节流，默认为 true
     * @param options.throttleWait - 节流的等待时间（毫秒），默认为 16ms
     * @param options.preventDefault - 是否阻止滚轮事件的默认行为（如页面滚动），默认为 true
     * @param options.preventCtrlWheelZoom - 是否禁用 ctrl+滚轮的浏览器缩放，默认为 true
     */
    constructor(
        target?: IListenable & IMeasurable,
        options?: {
            useThrottle?: boolean;
            throttleWait?: number;
            preventDefault?: boolean;
            preventCtrlWheelZoom?: boolean;
        }
    ) {
        this.domWrapper = target || document.body;

        // 应用选项配置
        this.useThrottle = options?.useThrottle ?? DEFAULT_USE_THROTTLE;
        this.throttleWait = options?.throttleWait ?? DEFAULT_THROTTLE_WAIT;
        this.preventDefault = options?.preventDefault ?? DEFAULT_PREVENT_DEFAULT;
        this.preventCtrlWheelZoom = options?.preventCtrlWheelZoom ?? DEFAULT_PREVENT_CTRL_WHEEL_ZOOM;

        // 绑定事件处理方法
        this._wheelHandler = this.handleWheel.bind(this);
        this._throttledWheelHandler = throttle(this._wheelHandler, this.throttleWait, {
            leading: true,
            trailing: true
        });

        // 添加事件监听器
        const wheelListener = this.useThrottle ? this._throttledWheelHandler : this._wheelHandler;
        this.domWrapper.addEventListener('wheel', wheelListener, { passive: !this.preventDefault });
    }

    /**
     * 处理原始的滚轮事件
     * @param e - WheelEvent 对象
     */
    private handleWheel(e: WheelEvent): void {
        // 禁用 ctrl+滚轮缩放逻辑
        if (this.preventCtrlWheelZoom && e.ctrlKey) {
            e.preventDefault();
        } else if (this.preventDefault) {
            e.preventDefault();
        }

        const { x: mouseX, y: mouseY } = this.getRelativeMousePosition(e);

        const eventData: WheelEventData = {
            originalEvent: e,
            mouseX,
            mouseY,
            deltaX: e.deltaX,
            deltaY: e.deltaY,
            deltaZ: e.deltaZ,
            deltaMode: e.deltaMode,
            ctrlKey: e.ctrlKey,
        };

        this.wheelEventsEmitter.emit(WheelEvents.SCROLL, eventData);
    }

    /**
     * 注册事件监听器
     * @param eventName - 事件名称，目前仅支持 WheelEvents.SCROLL
     * @param listener - 事件处理函数
     * @returns this 当前 WheelManager 实例
     */
    on(eventName: WheelEvents, listener: EventHandler): this {
        this.wheelEventsEmitter.on(eventName, listener);
        return this;
    }

    /**
     * 注销事件监听器
     * @param eventName - 事件名称
     * @param listener - 事件处理函数
     * @returns this 当前 WheelManager 实例
     */
    off(eventName: WheelEvents, listener: EventHandler): this {
        this.wheelEventsEmitter.off(eventName, listener);
        return this;
    }

    /**
     * 注册一次性的事件监听器
     * @param eventName - 事件名称
     * @param listener - 事件处理函数
     * @returns this 当前 WheelManager 实例
     */
    once(eventName: WheelEvents, listener: EventHandler): this {
        this.wheelEventsEmitter.once(eventName, listener);
        return this;
    }

    /**
     * 设置是否使用节流
     * @param use - true 表示使用，false 表示不使用
     */
    setUseThrottle(use: boolean): void {
        if (this.useThrottle === use) return;

        const oldListener = this.useThrottle ? this._throttledWheelHandler : this._wheelHandler;
        this.domWrapper.removeEventListener('wheel', oldListener);

        this.useThrottle = use;
        const newListener = this.useThrottle ? this._throttledWheelHandler : this._wheelHandler;
        this.domWrapper.addEventListener('wheel', newListener, { passive: !this.preventDefault });
    }

    /**
     * 设置节流等待时间（毫秒）
     * @param wait - 等待时间
     */
    setThrottleWait(wait: number): void {
        this.throttleWait = wait;
        // 重新创建节流函数
        const oldListener = this._throttledWheelHandler;
        const currentlyUsingThrottled = this.useThrottle && (this.domWrapper as any).__events?.wheel?.includes(oldListener); // 简陋的检查方式

        if (currentlyUsingThrottled) {
            this.domWrapper.removeEventListener('wheel', oldListener);
        }

        this._throttledWheelHandler = throttle(this._wheelHandler, this.throttleWait, {
            leading: true,
            trailing: true
        });

        if (currentlyUsingThrottled) {
            this.domWrapper.addEventListener('wheel', this._throttledWheelHandler, { passive: !this.preventDefault });
        }
    }

    /**
     * 设置是否阻止滚轮事件的默认行为
     * @param prevent - true 表示阻止，false 表示不阻止
     */
    setPreventDefault(prevent: boolean): void {
        if (this.preventDefault === prevent) return;

        // 移除旧的监听器
        const currentListener = this.useThrottle ? this._throttledWheelHandler : this._wheelHandler;
        this.domWrapper.removeEventListener('wheel', currentListener);

        this.preventDefault = prevent;

        // 添加新的监听器，并根据 preventDefault 设置 passive 选项
        this.domWrapper.addEventListener('wheel', currentListener, { passive: !this.preventDefault });
    }

    /**
     * 设置是否禁用 ctrl+滚轮的浏览器缩放
     * @param prevent - true 表示禁用，false 表示允许
     */
    setPreventCtrlWheelZoom(prevent: boolean): void {
        this.preventCtrlWheelZoom = prevent;
    }

    /**
     * 清理资源，移除事件监听器和所有通过 EventEmitter 注册的监听器
     */
    dispose(): void {
        const currentListener = this.useThrottle ? this._throttledWheelHandler : this._wheelHandler;
        this.domWrapper.removeEventListener('wheel', currentListener);
        this.wheelEventsEmitter.removeAllListeners();
    }

    /**
     * 获取鼠标相对于 domWrapper 左上角的坐标
     * @param e - 鼠标事件 (WheelEvent, MouseEvent 等)
     * @returns 相对坐标对象 { x, y }
     */
    private getRelativeMousePosition(e: MouseEvent | WheelEvent): { x: number, y: number } {
        const containerRect = this.domWrapper.getBoundingClientRect();
        return {
            x: e.clientX - containerRect.left,
            y: e.clientY - containerRect.top
        };
    }
}